import { useState } from 'react';
import { Link } from 'react-router-dom';
import './CarInsuranceAdLandingContent.css';

const STEPS = [
  {
    title: 'Share your info',
    body: 'Takes under a minute',
  },
  {
    title: 'We shop carriers for you',
    body: 'We shop and compare rates from top insurance providers for you.',
  },
  {
    title: 'You pick, we handle the rest',
    body: 'We explain the tradeoffs and help you enroll.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'Are you an insurance company?',
    answer:
      'No — we are a Texas insurance brokerage. We work with multiple carriers on your behalf, like a personal shopper for insurance.',
  },
  {
    question: 'Is this really free?',
    answer:
      'Yes. You do not pay GetSkimp to shop for you. We earn a standard commission from the carrier only if you choose to buy a policy.',
  },
  {
    question: 'How fast will I hear back?',
    answer: 'Most Texans hear from us within one business day with options to review.',
  },
  {
    question: 'What if I already have insurance?',
    answer:
      'We can often beat your renewal rate. Bring your current declarations page or we will gather what we need.',
  },
];

export function CarInsuranceAdLandingHero() {
  return (
    <div className="car-ad-hero">
      <p className="car-ad-eyebrow">Texas car insurance</p>
      <h1 className="car-ad-hero-title">We shop multiple carriers for you, for free.</h1>
      <Link to="/get-quote?type=car" className="btn btn-primary car-ad-hero-cta">
        Click here to get started
      </Link>
      <ul className="car-ad-hero-benefits" aria-label="Key benefits">
        <li>Multiple carriers compared</li>
        <li>Best coverage for the lowest price</li>
        <li>Save up to $500 and 4 hours of searching for the best deal</li>
      </ul>
    </div>
  );
}

export function CarInsuranceAdLandingDetails() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex((current) => (current === index ? null : index));
  };

  return (
    <div className="car-ad-landing-details">
      <section className="car-ad-section">
        <h2 className="car-ad-section-title">How it works</h2>
        <div className="car-ad-steps">
          {STEPS.map((step, index) => (
            <div key={step.title} className="car-ad-step">
              <div className="car-ad-step-number">{index + 1}</div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="car-ad-section car-ad-compare-section">
        <h2 className="car-ad-section-title">Why GetSkimp vs. going direct</h2>
        <div className="car-ad-compare-grid">
          <div className="card car-ad-compare-card car-ad-compare-card--muted">
            <h3>Going direct to one carrier</h3>
            <ul>
              <li>You see one company&apos;s rates</li>
              <li>You re-enter info everywhere</li>
              <li>Renewal hikes sneak up on you</li>
              <li>Hard to know if you are overpaying</li>
            </ul>
          </div>
          <div className="card car-ad-compare-card car-ad-compare-card--highlight">
            <h3>GetSkimp shops for you</h3>
            <ul>
              <li>One form, multiple carriers compared</li>
              <li>We explain the tradeoffs and help you enroll</li>
              <li>We keep looking for better deals after you enroll</li>
              <li>Your data is never sold</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="car-ad-section car-ad-faq-section">
        <h2 className="car-ad-section-title">Common questions</h2>
        <div className="car-ad-faq-list">
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openFaqIndex === index;
            const panelId = `car-ad-faq-panel-${index}`;
            const triggerId = `car-ad-faq-trigger-${index}`;

            return (
              <div key={item.question} className={`car-ad-faq-item${isOpen ? ' car-ad-faq-item--open' : ''}`}>
                <button
                  type="button"
                  id={triggerId}
                  className="car-ad-faq-trigger"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{item.question}</span>
                  <span className={`car-ad-faq-chevron${isOpen ? ' car-ad-faq-chevron--open' : ''}`} aria-hidden />
                </button>
                {isOpen && (
                  <div id={panelId} role="region" aria-labelledby={triggerId} className="car-ad-faq-panel">
                    <p>{item.answer}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
